import React, { useState, useMemo, useCallback } from 'react';
import PaymentOrderForm from '../../components/Form/PaymentOrderForm';
import PaymentOrderLetterGenerator from '../../components/HR/PaymentOrderLetterGenerator';
import Button from '../../components/Form/Button';
import { PageHeader, FilterBar, ErrorState } from '../../components/Common';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { bumpCodePrefixSequence } from '../../utils/codePrefixUtils';
import { PaymentOrder } from '../../types';
import { usePaymentOrders, useCreatePaymentOrder, useUpdatePaymentOrder, useDeletePaymentOrder } from '../../hooks/usePaymentOrders';
import { usePaymentOrderStatus } from './usePaymentOrderStatus';
import { usePaymentOrderFilters } from './usePaymentOrderFilters';
import PaymentOrderItemsRow from './PaymentOrderItemsRow';
import { useProviders } from '../../hooks/useProviders';
import { useBanks } from '../../hooks/useBanks';
import { useCurrencies } from '../../hooks/useCurrencies';
import { useToast } from '../../contexts/ToastContext';
import { resolveCurrencyCode } from '../../utils/currencyUtils';

const PaymentOrders: React.FC = () => {
  const { showToast } = useToast();
  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 1000 }
  }), []);

  const { data: paymentOrders = [], isLoading, error, refetch } = usePaymentOrders(queryOptions);
  const { data: providers = [] } = useProviders(queryOptions);
  const { data: banks = [] } = useBanks(queryOptions);
  const { data: currencies = [] } = useCurrencies(queryOptions);

  const createMutation = useCreatePaymentOrder(() => {
    showToast('Payment order created successfully', 'success');
    refetch();
    setShowForm(false);
    setEditingPaymentOrder(undefined);
  });
  const updateMutation = useUpdatePaymentOrder(() => {
    showToast('Payment order updated successfully', 'success');
    refetch();
    setShowForm(false);
    setEditingPaymentOrder(undefined);
  });
  const deleteMutation = useDeletePaymentOrder(() => {
    showToast('Payment order deleted successfully', 'success');
    refetch();
  });
  const { setOrderStatus, setItemStatus } = usePaymentOrderStatus(refetch);

  const [showForm, setShowForm] = useState(false);
  const [editingPaymentOrder, setEditingPaymentOrder] = useState<PaymentOrder | undefined>();
  const [showLetterGenerator, setShowLetterGenerator] = useState(false);
  const [selectedPaymentOrderForLetter, setSelectedPaymentOrderForLetter] = useState<PaymentOrder | undefined>();
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

  const getProviderName = useCallback((id: string) => providers.find(p => p.id === id)?.name ?? '-', [providers]);
  const getBankName = useCallback((id?: string) => (!id ? '-' : banks.find(b => b.id === id)?.name ?? '-'), [banks]);

  const { searchTerm, setSearchTerm, filteredPaymentOrders, clearFilters, filterConfigs } =
    usePaymentOrderFilters(paymentOrders, providers, banks, currencies, getProviderName, getBankName);

  const exportData = useMemo(() => filteredPaymentOrders.map(order => ({
    'Order Number': order.orderNumber, 'Provider': getProviderName(order.providerId), 'Bank': getBankName(order.bankId),
    'Date': new Date(order.date).toLocaleDateString(), 'Amount': order.total, 'Currency': resolveCurrencyCode(order.currency),
    'Status': order.status, 'Requested By': order.requestedBy
  })), [filteredPaymentOrders, getProviderName, getBankName]);
  const handleExport = () => exportToExcel(exportData, `payment_orders_${new Date().toISOString().split('T')[0]}`);
  const handlePrint = () => printAsExcel(exportData, 'Payment Orders');

  const handleEdit = (paymentOrder: PaymentOrder) => {
    setEditingPaymentOrder(paymentOrder);
    setShowForm(true);
  };
  const handleDelete = async (paymentOrder: PaymentOrder) => {
    if (window.confirm(`Are you sure you want to delete payment order ${paymentOrder.orderNumber}?`)) {
      try { await deleteMutation.execute(paymentOrder); } catch (e) { console.error('Delete payment order error:', e); }
    }
  };
  const handleAdd = () => { setEditingPaymentOrder(undefined); setShowForm(true); };
  const handleFormSubmit = async (data: Partial<PaymentOrder>) => {
    if (editingPaymentOrder) {
      try {
        await updateMutation.execute({ id: parseInt(editingPaymentOrder.id), data: { ...editingPaymentOrder, ...data } });
      } catch (e) { console.error('Update payment order error:', e); }
    } else {
      try { await createMutation.execute(data); await bumpCodePrefixSequence(data.orderNumber); } catch (e) { console.error('Create payment order error:', e); }
    }
  };
  const handleFormCancel = () => { setShowForm(false); setEditingPaymentOrder(undefined); };
  const handleGenerateLetter = (paymentOrder: PaymentOrder) => {
    setSelectedPaymentOrderForLetter(paymentOrder);
    setShowLetterGenerator(true);
  };
  const handleLetterCancel = () => { setShowLetterGenerator(false); setSelectedPaymentOrderForLetter(undefined); };
  const toggleItems = (order: PaymentOrder) =>
    setExpandedOrderId(expandedOrderId === order.id ? null : order.id);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ocean-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payment orders...</p>
        </div>
      </div>
    );
  }
  if (error) return <ErrorState message="Error loading payment orders" onRetry={refetch} entityName="payment orders" />;
  if (showForm) return (
    <div className="space-y-6">
      <PaymentOrderForm paymentOrder={editingPaymentOrder} onSubmit={handleFormSubmit} onCancel={handleFormCancel} />
    </div>
  );
  if (showLetterGenerator && selectedPaymentOrderForLetter) {
    return (
      <PaymentOrderLetterGenerator
        paymentOrder={selectedPaymentOrderForLetter}
        onClose={handleLetterCancel}
        onGenerate={() => handleLetterCancel()}
      />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Payment Orders" description="Manage your payment orders and generate payment documents" />
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search payment orders..."
        filters={filterConfigs}
        onClearFilters={clearFilters}
        totalCount={paymentOrders.length}
        filteredCount={filteredPaymentOrders.length}
        entityName="Payment Orders"
        onAdd={handleAdd}
        addLabel="Add New"
        onExport={handleExport}
        onPrint={handlePrint}
      />
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Order No.', 'Date', 'Provider', 'Total', 'Payment Method', 'Status', 'Requested By', 'Actions'].map((h) => (
                  <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPaymentOrders.map((order) => (
                <React.Fragment key={order.id}>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{order.orderNumber}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(order.date).toLocaleDateString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{getProviderName(order.providerId)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{resolveCurrencyCode(order.currency)} {order.total.toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex flex-col">
                      <span>{order.paymentMethod.replace('_', ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}</span>
                      {order.paymentMethod === 'bank_transfer' && order.bankId && <span className="text-xs text-gray-500">{getBankName(order.bankId)}</span>}
                      {order.paymentMethod === 'check' && order.checkNumber && <span className="text-xs text-gray-500">Check: {order.checkNumber}</span>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      order.status === 'paid' ? 'bg-green-100 text-green-800' :
                      order.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                      order.status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{order.requestedBy}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-y-2">
                    <div className="flex space-x-2">
                      <Button onClick={() => handleEdit(order)} variant="secondary" size="sm">Edit</Button>
                      <Button onClick={() => handleDelete(order)} variant="danger" size="sm">Delete</Button>
                    </div>
                    <div className="flex space-x-2">
                      <Button onClick={() => toggleItems(order)} variant="secondary" size="sm" className="w-full">
                        {expandedOrderId === order.id ? 'Hide Items' : `Items (${order.items?.length || 0})`}
                      </Button>
                    </div>
                    <div className="flex space-x-2">
                      <Button onClick={() => handleGenerateLetter(order)} variant="primary" size="sm" className="w-full">Generate Letter</Button>
                    </div>
                    {order.status === 'pending' && (
                      <Button onClick={() => setOrderStatus(order, 'approved')} variant="primary" size="sm" className="w-full text-xs">Approve All</Button>
                    )}
                    {order.status === 'approved' && (
                      <Button onClick={() => setOrderStatus(order, 'paid')} variant="success" size="sm" className="w-full text-xs">Mark Purchased</Button>
                    )}
                  </td>
                </tr>
                {expandedOrderId === order.id && (
                  <PaymentOrderItemsRow order={order} colSpan={8} onItemStatus={setItemStatus} />
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          {filteredPaymentOrders.length === 0 && (
            <div className="text-center py-12"><p className="text-gray-500">No payment orders found</p></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentOrders;
